import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Save, X } from 'lucide-react';
import apiClient from '../api/client';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { useToast } from '../components/ui/Toast';

/**
 * "Vehicle data" — the record the customer keeps for their own vehicle: plate, brand, type,
 * odometer, MOT (contrôle technique) expiry and VIN.
 *
 * Persisted against the caller's company via /me/vehicles, which takes the customerId from
 * the JWT — the dialog never says which account it is writing to. The plate is the identity:
 * saving a plate the account already holds updates that record, so re-opening the dialog and
 * pressing Save is an edit, not a duplicate.
 */

export interface Vehicle {
  id: string;
  licensePlate: string;
  brand: string | null;
  type: string | null;
  kmPosition: number | null;
  motDate: string | null;
  vin: string | null;
  updatedAt: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called with the saved record once the POST succeeds. */
  onSaved?: (v: Vehicle) => void;
}

interface FormState {
  licensePlate: string;
  brand: string;
  type: string;
  kmPosition: string;
  motDate: string;
  vin: string;
}

const EMPTY: FormState = {
  licensePlate: '',
  brand: '',
  type: '',
  kmPosition: '',
  motDate: '',
  vin: '',
};

const toForm = (v: Vehicle): FormState => ({
  licensePlate: v.licensePlate,
  brand: v.brand ?? '',
  type: v.type ?? '',
  kmPosition: v.kmPosition == null ? '' : String(v.kmPosition),
  motDate: v.motDate ?? '',
  vin: v.vin ?? '',
});

const FIELDS: { key: keyof FormState; type?: string; inputMode?: 'numeric' }[] = [
  { key: 'licensePlate' },
  { key: 'brand' },
  { key: 'type' },
  { key: 'kmPosition', type: 'number', inputMode: 'numeric' },
  { key: 'motDate', type: 'date' },
  { key: 'vin' },
];

export function VehicleDialog({ open, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const { push, showApiError } = useToast();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef<HTMLInputElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  // Open with whatever is already on file (most recently saved first), so the dialog shows
  // the customer their stored data instead of a blank form they've filled in once already.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setForm(EMPTY);
    void (async () => {
      try {
        const { data } = await apiClient.get<{ data: Vehicle[] }>('/me/vehicles');
        if (!cancelled && data.data?.length) setForm(toForm(data.data[0]));
      } catch {
        // A prefill failure is not worth a toast — the operator can still type the record in.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    firstRef.current?.focus();
    return () => restoreRef.current?.focus?.();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) {
        e.preventDefault();
        onClose();
        return;
      }
      // Focus trap — Tab cycles inside the panel rather than escaping to the page behind.
      if (e.key !== 'Tab') return;
      const f = panelRef.current?.querySelectorAll<HTMLElement>(
        'input:not([disabled]), button:not([disabled])'
      );
      if (!f?.length) return;
      const [first, last] = [f[0], f[f.length - 1]];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const set = (k: keyof FormState) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.licensePlate.trim()) {
      push({
        tone: 'warning',
        title: t('vehicle.plateRequired'),
        message: t('vehicle.plateRequiredDetail'),
      });
      firstRef.current?.focus();
      return;
    }
    setSaving(true);
    try {
      const { data } = await apiClient.post<{ data: Vehicle }>('/me/vehicles', {
        licensePlate: form.licensePlate,
        brand: form.brand,
        type: form.type,
        kmPosition: form.kmPosition,
        motDate: form.motDate,
        vin: form.vin,
      });
      push({ tone: 'success', title: t('vehicle.saved'), message: data.data.licensePlate });
      onSaved?.(data.data);
      onClose();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => !saving && onClose()}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vehicle-dialog-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md overflow-hidden rounded-lg border bg-card shadow-lg"
      >
        <form onSubmit={onSubmit}>
          <header className="flex items-center justify-between px-6 py-4">
            <h2 id="vehicle-dialog-title" className="text-lg font-bold text-foreground">
              {t('vehicle.title')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              aria-label={t('vehicle.close')}
              className="text-muted-foreground hover:text-foreground"
            >
              <X size={18} />
            </button>
          </header>

          <div className="max-h-[60vh] space-y-4 overflow-y-auto px-6 pb-5">
            {FIELDS.map((f, i) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`vehicle-${f.key}`}>{t(`vehicle.fields.${f.key}`)}</Label>
                <Input
                  id={`vehicle-${f.key}`}
                  ref={i === 0 ? firstRef : undefined}
                  type={f.type}
                  inputMode={f.inputMode}
                  min={f.type === 'number' ? 0 : undefined}
                  // A date input shows its own placeholder (dd/mm/yyyy); the rest echo the label.
                  placeholder={f.type === 'date' ? undefined : t(`vehicle.fields.${f.key}`)}
                  value={form[f.key]}
                  onChange={set(f.key)}
                  disabled={saving}
                  className="h-10"
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 border-t bg-muted/30 px-6 py-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              <X className="h-4 w-4" />
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={saving}>
              <Save className="h-4 w-4" />
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
